// @ts-ignore
import React from 'react'
// @ts-ignore
import { useSpring, animated } from '@react-spring/web'
import { useTheme } from 'styled-components'
import styled from 'styled-components'
import { ResponsiveBump } from '@nivo/bump'
import { BasicTooltip } from '@nivo/tooltip'

export interface RankingChartDatum {
    // year
    x: number
    // rank
    y: number
    // percentage attached to a specific year
    // used to compute the rank
    percentage_question: number
}

export interface RankingChartSerie {
    id: string
    name: string
    data: RankingChartDatum[]
}

interface CustomPointProps {
    point: Point
}

interface Point {
    x: number
    y: number
    isInactive: boolean
    size: number
    borderColor: string
    borderWidth: number
    data: RankingChartDatum
}

const CustomPoint = (props: CustomPointProps) => {
    const { x, y, data, isInactive, size, borderColor, borderWidth } = props.point
    const theme = useTheme()

    const transition = useSpring({
        transform: `translate(${x}, ${y})`,
        radius: size / 2
    })

    return (
        <animated.g transform={transition.transform} style={{ pointerEvents: 'none' }}>
            <animated.circle cy={size / 5} fill="rgba(0, 0, 0, .2)" />
            <animated.circle
                r={transition.radius}
                fill={theme.colors.background}
                stroke={borderColor}
                strokeWidth={borderWidth}
            />
            {!isInactive && (
                <text textAnchor="middle" y={4} fill={theme.colors.text} fontSize="11px">
                    {Math.round(data?.percentage_question)}%
                </text>
            )}
        </animated.g>
    )
}

interface CustomTooltipProps {
    name: string
    color: string
    getName: func
}

const CustomTooltip = ({ id, color, getName }: CustomTooltipProps) => (
    <BasicTooltip id={getName(id)} enableChip={true} color={color} />
)

interface RankingChartProps {
    data: RankingChartSerie[]
}

function repairData(data: RankingChartSerie[]) {
    /*
        this method will fill the data with empty years, 
        it may happen when some answers were removed 
        or added in different years
    */
   const collectInformationAboutYears = (data: RankingChartSerie[]) => {
       const years = new Set<number>()
       for (const serie of data) {
           for (const datum of serie.data) {
               years.add(datum.x)
           }
       }
       return years
   }
   const years = collectInformationAboutYears(data) as any;
    for (const serie of data) {
         for (const year of years) {
              const datum = serie.data.find(datum => datum.x === year)
              if (!datum) {
                serie.data.push({
                     x: year,
                     y: null as any,
                     percentage_question: 0
                })
              }
         }
         serie.data.sort((a, b) => a.x - b.x)
    }
    return data
}

export const RankingChart = ({ data }: RankingChartProps) => {
    const theme = useTheme()
    const getName = (entityId: string) => data?.find(series => series.id === entityId)?.name
    const filteredData = repairData(data)
    return (
        <ResponsiveBump
            data={filteredData}
            margin={{ top: 40, right: 170, bottom: 40, left: 170 }}
            colors={theme.colors.distinct}
            // @ts-ignore
            inactiveLineWidth={5}
            theme={theme.charts}
            enableGridX={true}
            enableGridY={false}
            axisTop={{
                tickSize: 0,
                tickPadding: 9
            }}
            axisRight={null}
            axisBottom={{
                tickSize: 0,
                tickPadding: 9
            }}
            axisLeft={null}
            startLabel={d => d.name}
            startLabelTextColor={'#001a72'}
            endLabel={d => d.name}
            endLabelTextColor={'#001a72'}
            pointComponent={CustomPoint}
            lineWidth={5}
            pointSize={36}
            pointBorderWidth={3}
            pointBorderColor={{ from: 'serie.color' }}
            activeLineWidth={8}
            activePointSize={60}
            activePointBorderWidth={4}
            inactivePointSize={0}
            inactivePointBorderWidth={2}
            // @ts-ignore
            tooltip={({ serie }) => <CustomTooltip {...serie} getName={getName} />}
        />
    )
}

const NoShadowContainer = styled.div``
